import { getActions } from '../global';

import { DEBUG, DEBUG_MORE, IS_TEST } from '../config';
// eslint-disable-next-line import-x/default
import serviceWorkerUrl from '../serviceWorker/service.worker.ts?worker&url';
import { IS_ANDROID, IS_IOS, IS_SERVICE_WORKER_SUPPORTED } from './browser/windowEnvironment';
import { formatShareText } from './deeplink';
import { validateFiles } from './files';
import { notifyClientReady, playNotifySoundDebounced } from './notifications';

type WorkerAction = {
  type: string;
  payload: Record<string, any>;
};

const IGNORE_WORKER_PATH = '/k/';
// How long to wait for a freshly-registered SW to take control (clients.claim → controllerchange).
const SW_CONTROL_TIMEOUT = 4000;
// In dev the worker is served from a deep path (/src/serviceWorker/...), so a relative './' scope
// resolves to that directory and the SW never controls '/progressive/…' media requests (audio/voice
// silently fail). Force root scope — the Vite dev server sends `Service-Worker-Allowed: /`, which
// permits it. In prod the worker is emitted at the root, so the default scope already covers '/'.
const SERVICE_WORKER_OPTIONS: RegistrationOptions = import.meta.env.DEV
  ? { scope: '/', type: 'module' }
  : { type: 'module' };

function handleWorkerMessage(e: MessageEvent) {
  const action: WorkerAction = e.data;
  if (DEBUG_MORE) {
    // eslint-disable-next-line no-console
    console.log('[SW] Message from worker', action);
  }
  if (!action.type) return;
  const dispatch = getActions();
  const payload = action.payload;
  switch (action.type) {
    case 'focusMessage':
      dispatch.focusMessage?.(payload as any);
      break;
    case 'playNotificationSound':
      playNotifySoundDebounced(action.payload.id);
      break;
    case 'share':
      dispatch.openChatWithDraft({
        text: formatShareText(payload.url, payload.text, payload.title),
        files: validateFiles(payload.files),
      });
      break;
  }
}

function subscribeToWorker() {
  navigator.serviceWorker.removeEventListener('message', handleWorkerMessage);
  navigator.serviceWorker.addEventListener('message', handleWorkerMessage);
  // Notify web worker that client is ready to receive messages
  notifyClientReady();
}

// Register in dev too: media streaming (voice/audio/video) is served by the SW via /progressive/,
// so without it ALL media playback is dead in dev (the app still builds /progressive/ URLs —
// IS_PROGRESSIVE_SUPPORTED only checks browser support). The stale-cache pain that once motivated
// disabling it in dev is addressed inside the worker: asset caching is skipped in dev builds.
if (IS_SERVICE_WORKER_SUPPORTED) {
  window.addEventListener('load', async () => {
    try {
      const controller = navigator.serviceWorker.controller;
      if (!controller || controller.scriptURL.includes(IGNORE_WORKER_PATH)) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const ourRegistrations = registrations.filter((r) => !r.scope.includes(IGNORE_WORKER_PATH));
        if (ourRegistrations.length) {
          if (DEBUG) {
            // eslint-disable-next-line no-console
            console.log('[SW] Hard reload detected, re-enabling Service Worker');
          }
          await Promise.all(ourRegistrations.map((r) => r.unregister()));
        }
      }

      const registration = await navigator.serviceWorker.register(serviceWorkerUrl, SERVICE_WORKER_OPTIONS);

      // Force SW update check — ensures new builds activate immediately
      registration.update().catch(() => {});

      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[SW] ServiceWorker registered');
      }

      await navigator.serviceWorker.ready;

      // Wait for registration to be available
      await navigator.serviceWorker.getRegistration();

      // A freshly-registered SW does not control THIS page until clients.claim() propagates, which
      // arrives as a `controllerchange` event slightly after registration. Wait for it before
      // declaring the worker unavailable — otherwise the very first load always logs a false
      // "not available" and pops the SERVICE_WORKER_DISABLED dialog.
      const isControlled = navigator.serviceWorker.controller
        ? true
        : await new Promise<boolean>((resolve) => {
          const onChange = () => {
            navigator.serviceWorker.removeEventListener('controllerchange', onChange);
            resolve(true);
          };
          navigator.serviceWorker.addEventListener('controllerchange', onChange);
          setTimeout(() => {
            navigator.serviceWorker.removeEventListener('controllerchange', onChange);
            resolve(Boolean(navigator.serviceWorker.controller));
          }, SW_CONTROL_TIMEOUT);
        });

      if (isControlled) {
        if (DEBUG) {
          // eslint-disable-next-line no-console
          console.log('[SW] ServiceWorker ready');
        }
        subscribeToWorker();
      } else {
        if (DEBUG) {
          // eslint-disable-next-line no-console
          console.error('[SW] ServiceWorker not available');
        }

        if (!IS_IOS && !IS_ANDROID && !IS_TEST) {
          getActions().showDialog?.({ data: { type: 'error', message: 'SERVICE_WORKER_DISABLED', hasErrorKey: true } });
        }
      }
    } catch (err) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.error('[SW] ServiceWorker registration failed: ', err);
      }
    }
  });
  window.addEventListener('focus', async () => {
    await navigator.serviceWorker.ready;
    subscribeToWorker();
  });
}
